import {
  AuthoritativeGame,
  DEFAULT_GAME_CONFIG,
  FIXED_DT_SECONDS,
  TICK_RATE,
  type GameEvent,
  type GameState,
  type PublicBodyState,
} from "@air-hockey/game-core";
import { clamp } from "./math";
import type { RenderState, SimulationAdapter, SimulationEvent, Vector2 } from "./types";

export const PRACTICE_TABLE = {
  width: 1_000,
  height: 1_600,
  rail: 40,
  cornerRadius: 140,
  goalWidth: 360,
  puckRadius: 32,
  malletRadius: 72,
  centerClearance: 8,
} as const;

const SCORE_TO_WIN = 7;
const MATCH_DURATION_MS = 120_000;

interface PracticeSimulationOptions {
  scoreToWin?: number;
  durationMs?: number;
  countdownMs?: number;
}

/**
 * Local practice runs the exact shared authoritative core in-process. The
 * adapter is still client-only: online score, goals, and winner always come
 * from Durable Object messages rather than this instance.
 */
export class PracticeSimulation implements SimulationAdapter {
  readonly kind = "practice" as const;

  private readonly listeners = new Set<(event: SimulationEvent) => void>();
  private readonly game: AuthoritativeGame;
  private localTarget: Vector2 = { x: 0.5, y: 0.79 };
  private botTarget: Vector2 = { x: 0.5, y: 0.21 };
  private accumulator = 0;
  private localSequence = 0;
  private botSequence = 0;
  private botThinkTicks = 0;
  private elapsedMs = 0;
  private touches = 0;
  private maxPuckSpeed = 0;
  private disposed = false;

  constructor({
    scoreToWin = SCORE_TO_WIN,
    durationMs = MATCH_DURATION_MS,
    countdownMs = 3_000,
  }: PracticeSimulationOptions = {}) {
    this.game = new AuthoritativeGame({
      matchId: "local-practice",
      seed: 0x41c0de,
      config: {
        rules: {
          mode: "timed",
          pointsToWin: scoreToWin,
          durationTicks: Math.max(1, Math.round((durationMs / 1_000) * TICK_RATE)),
          initialCountdownTicks: Math.max(0, Math.round((countdownMs / 1_000) * TICK_RATE)),
          restartCountdownTicks: TICK_RATE * 2,
          goalPauseTicks: TICK_RATE,
        },
      },
    });
    this.game.setConnected("PLAYER_A", true);
    this.game.setConnected("PLAYER_B", true);
    this.game.setReady("PLAYER_A", true);
    this.game.setReady("PLAYER_B", true);
  }

  getState(): RenderState {
    const state = this.game.state;
    const local = state.mallets.PLAYER_A;
    const opponent = state.mallets.PLAYER_B;
    const winner =
      state.winner === null ? null : state.winner === "PLAYER_A" ? "local" : "opponent";

    return {
      phase: state.phase,
      local: this.normalizedBody({
        x: local.position.x,
        y: local.position.y,
        vx: local.velocity.x,
        vy: local.velocity.y,
      }),
      opponent: this.normalizedBody({
        x: opponent.position.x,
        y: opponent.position.y,
        vx: opponent.velocity.x,
        vy: opponent.velocity.y,
      }),
      puck: this.normalizedBody({
        x: state.puck.position.x,
        y: state.puck.position.y,
        vx: state.puck.velocity.x,
        vy: state.puck.velocity.y,
      }),
      scoreLocal: state.score.PLAYER_A,
      scoreOpponent: state.score.PLAYER_B,
      remainingTimeMs: state.remainingTicks === null ? null : state.remainingTicks * (1_000 / TICK_RATE),
      countdown:
        state.phase === "countdown"
          ? Math.max(1, Math.ceil(state.phaseTicksRemaining / TICK_RATE))
          : null,
      lastGoal:
        state.lastGoal === null
          ? null
          : state.lastGoal.scorer === "PLAYER_A"
            ? "local"
            : "opponent",
      winner,
      finishReason: state.finishReason,
      stats: {
        touches: this.touches,
        maxPuckSpeed: this.maxPuckSpeed,
        elapsedMs: this.elapsedMs,
      },
    };
  }

  setLocalTarget(target: Vector2): void {
    this.localTarget = {
      x: clamp(target.x, 0, 1),
      y: clamp(target.y, 0.5, 1),
    };
  }

  step(deltaSeconds: number): void {
    if (this.disposed) return;
    this.accumulator += clamp(deltaSeconds, 0, 0.05);
    while (this.accumulator >= FIXED_DT_SECONDS) {
      this.fixedUpdate();
      this.accumulator -= FIXED_DT_SECONDS;
    }
  }

  subscribe(listener: (event: SimulationEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    this.disposed = true;
    this.listeners.clear();
  }

  private fixedUpdate(): void {
    const before = this.game.state;
    if (before.phase === "finished") return;

    this.updateBotIntent(before);
    if (before.phase === "playing") {
      this.localSequence += 1;
      this.botSequence += 1;
      this.game.queueInput("PLAYER_A", {
        sequence: this.localSequence,
        clientTime: before.tick * (1_000 / TICK_RATE),
        targetX: this.localTarget.x,
        targetY: this.localTarget.y,
      });
      this.game.queueInput("PLAYER_B", {
        sequence: this.botSequence,
        clientTime: before.tick * (1_000 / TICK_RATE),
        targetX: this.botTarget.x,
        targetY: this.botTarget.y,
      });
    }

    const result = this.game.step();
    if (before.phase === "playing") this.elapsedMs += 1_000 / TICK_RATE;
    this.maxPuckSpeed = Math.max(
      this.maxPuckSpeed,
      Math.hypot(result.state.puck.velocity.x, result.state.puck.velocity.y),
    );
    result.events.forEach((event) => this.handleCoreEvent(event, result.state));
  }

  private updateBotIntent(state: GameState): void {
    this.botThinkTicks -= 1;
    if (this.botThinkTicks > 0) return;
    this.botThinkTicks = 7;

    const puck = state.puck;
    const table = this.game.config.table;
    const boundsTop = table.wallInset + this.game.config.mallet.radius;
    const boundsBottom = table.height / 2 - this.game.config.mallet.centerLineGap / 2 - this.game.config.mallet.radius;
    const puckThreatening = state.phase === "playing" && puck.position.y < table.height * 0.62;
    const predictionTime = clamp(
      (state.mallets.PLAYER_B.position.y - puck.position.y) / Math.max(500, Math.abs(puck.velocity.y)),
      0,
      0.28,
    );
    const imperfectRead = Math.sin(state.tick * 0.071) * 32;
    const targetX = puckThreatening
      ? puck.position.x + puck.velocity.x * predictionTime + imperfectRead
      : table.width / 2;
    const targetY = puckThreatening
      ? clamp(puck.position.y - 112, boundsTop, boundsBottom)
      : table.height * 0.21;

    this.botTarget = {
      x: clamp(targetX / table.width, 0, 1),
      y: clamp(targetY / table.height, 0, 0.5),
    };
  }

  private handleCoreEvent(event: GameEvent, state: GameState): void {
    if (event.type === "COLLISION") {
      const speed = Math.hypot(state.puck.velocity.x, state.puck.velocity.y);
      const strength = clamp(speed / DEFAULT_GAME_CONFIG.puck.maxSpeed, 0.08, 1);
      const pan = clamp((state.puck.position.x / PRACTICE_TABLE.width) * 2 - 1, -1, 1);
      if (event.collider === "mallet") {
        if (event.player === "PLAYER_A") this.touches += 1;
        this.emit({ type: "impact", strength, pan });
      } else {
        this.emit({ type: "wall", strength, pan });
      }
      return;
    }
    if (event.type === "GOAL") {
      this.emit({ type: "goal", scorer: event.scorer === "PLAYER_A" ? "local" : "opponent" });
      return;
    }
    if (event.type === "PHASE_CHANGED") {
      this.emit({ type: "phase", phase: event.to });
      return;
    }
    if (event.type === "FINISHED") {
      const winner = event.winner === null ? "draw" : event.winner === "PLAYER_A" ? "local" : "opponent";
      this.emit({ type: "finished", winner });
    }
  }

  private normalizedBody(body: PublicBodyState): PublicBodyState {
    return {
      x: body.x / PRACTICE_TABLE.width,
      y: body.y / PRACTICE_TABLE.height,
      vx: body.vx / PRACTICE_TABLE.width,
      vy: body.vy / PRACTICE_TABLE.height,
    };
  }

  private emit(event: SimulationEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }
}
